import { useState, useEffect } from 'react';

interface SidebarState {
  isCollapsed: boolean;
  isAutoCollapsed: boolean;
  isMobile: boolean;
  isTablet: boolean;
  shouldShow: boolean;
  toggleSidebar: () => void;
  forceCollapse: (collapsed: boolean) => void;
}

/**
 * Custom hook for managing auto-collapsing sidebar state
 * Handles responsive behavior and manual toggle functionality
 */
export const useSidebarState = (): SidebarState => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isAutoCollapsed, setIsAutoCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Check screen width and auto-collapse if needed (Supabase responsive pattern)
  useEffect(() => {
    const checkScreenWidth = () => {
      const width = window.innerWidth;
      const newIsMobile = width < 768;  // Mobile breakpoint
      const newIsTablet = width >= 768 && width < 1280;  // Tablet breakpoint (updated to match Supabase spec)
      const isDesktop = width >= 1280;  // Desktop breakpoint
      
      setIsMobile(newIsMobile);
      setIsTablet(newIsTablet);
      
      if (newIsMobile) {
        // Mobile: Hide secondary sidebar completely, primary sidebar becomes overlay
        setIsAutoCollapsed(true);
        setIsCollapsed(true);
      } else if (newIsTablet && !isAutoCollapsed) {
        // Tablet: Auto-collapse secondary sidebar to icon mode (56px width)
        setIsAutoCollapsed(true);
        setIsCollapsed(true);
      } else if (isDesktop && isAutoCollapsed) {
        // Desktop: Restore expanded state (both sidebars visible and expanded)
        setIsAutoCollapsed(false);
        setIsCollapsed(false);
      }
    };

    // Check on mount
    checkScreenWidth();

    // Add resize listener
    window.addEventListener('resize', checkScreenWidth);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenWidth);
    };
  }, [isAutoCollapsed]);

  const toggleSidebar = () => {
    // If auto-collapsed due to screen size, disable auto-collapse when manually toggling
    if (isAutoCollapsed) {
      setIsAutoCollapsed(false);
    }
    setIsCollapsed(!isCollapsed);
  };

  const forceCollapse = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
    if (!collapsed) {
      setIsAutoCollapsed(false);
    }
  };

  return {
    isCollapsed,
    isAutoCollapsed,
    isMobile,
    isTablet,
    shouldShow: !isMobile, // Hide completely on mobile
    toggleSidebar,
    forceCollapse,
  };
};